import { z } from 'zod';
import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  NetworkRequestBatchSchema,
  NetworkRequestSchema,
  TorchSeenNetworkInteractionResponseSchema,
  TorchSeenNetworkInteractionSchema,
  TrackerBatchReportResponseSchema,
  TrackerReportResponseSchema,
  TrackingDataResponseSchema,
  PageTrackingSchema,
  PageTrackingResponseSchema,
  ToolExecutionHarsResponseSchema,
  ToolExecutionHarSchema,
  ToolHarPresenceByThreadResponseSchema,
  DecodeGrpcHarPreviewBodySchema,
  DecodeGrpcHarPreviewResponseSchema,
  EncodeGrpcHarPreviewBodySchema,
  EncodeGrpcHarPreviewResponseSchema,
} from './tracker.schema';

export const trackerContract = terraContract.router({
  track: terraContract
    .meta({
      permissions: [PERMISSIONS.EXECUTE_TESTS.name],
    })
    .route({
      method: 'POST',
      path: '/track',
      summary: 'Track network requests and responses from Chrome extension',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: NetworkRequestSchema }))
    .output(TrackerReportResponseSchema),
  trackSeen: terraContract
    .meta({
      permissions: [PERMISSIONS.EXECUTE_TESTS.name],
    })
    .route({
      method: 'POST',
      path: '/track/seen',
      summary:
        'Record a Torch browser request that was not stored as HAR evidence',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: TorchSeenNetworkInteractionSchema }))
    .output(TorchSeenNetworkInteractionResponseSchema),
  trackBulk: terraContract
    .meta({
      permissions: [PERMISSIONS.EXECUTE_TESTS.name],
    })
    .route({
      method: 'POST',
      path: '/track/bulk',
      summary: 'Track network requests and responses in bulk',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: NetworkRequestBatchSchema }))
    .output(TrackerBatchReportResponseSchema),
  trackPages: terraContract
    .meta({
      permissions: [PERMISSIONS.EXECUTE_TESTS.name],
    })
    .route({
      method: 'POST',
      path: '/track-pages',
      summary: 'Track webpage captures from Chrome extension',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: PageTrackingSchema }))
    .output(PageTrackingResponseSchema),
  getTrackingData: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/tracking/{applicationId}',
      summary: 'Get tracking data for a project',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ applicationId: z.string() }) }))
    .output(TrackingDataResponseSchema),
  getToolHarsByThreadId: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/tool-hars/thread/{threadId}',
      summary: 'Get tool execution HARs by thread ID',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ threadId: z.string() }) }))
    .output(ToolExecutionHarsResponseSchema),
  getToolHarsByToolCallId: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/tool-hars/thread/{threadId}/tool-call/{toolCallId}',
      summary: 'Get tool execution HARs of a tool call within a thread',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({
          threadId: z.string().min(1),
          toolCallId: z.string().min(1),
        }),
      }),
    )
    .output(ToolExecutionHarsResponseSchema),
  getToolHarPresenceByThread: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/tool-hars/presence/thread/{threadId}',
      summary: 'Get tool call IDs with captured HTTP artifacts by thread ID',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ threadId: z.string() }) }))
    .output(ToolHarPresenceByThreadResponseSchema),
  getToolHarsByExecutionId: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/tool-hars/execution/{executionId}',
      summary: 'Get tool execution HARs by execution ID',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ executionId: z.string() }) }))
    .output(ToolExecutionHarsResponseSchema),
  getToolHarById: terraContract
    .meta({
      openApi: true,
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/tool-hars/{id}',
      summary: 'Get finding evidence HTTP traffic (HAR) by ID',
      description:
        'Retrieves the full captured HTTP request and response (HAR) for a piece of finding evidence. Evidence blocks of type "evidence" with `evidenceType: "http"` in a finding\'s `pocBlocks` include a `toolExecutionHarId`; pass that ID here to fetch the complete request/response traffic that backs the evidence.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({
          id: z
            .string()
            .describe(
              "Tool execution HAR ID, taken from an evidence block's `toolExecutionHarId` field in a finding's `pocBlocks`.",
            ),
        }),
      }),
    )
    .output(ToolExecutionHarSchema.nullable()),
  decodeGrpcHarPreview: terraContract
    .meta({
      permissions: [PERMISSIONS.EXECUTE_TESTS.name],
    })
    .route({
      method: 'POST',
      path: '/decode-grpc-har-preview',
      summary: 'Decode gRPC-web HAR request/response bodies without persisting',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: DecodeGrpcHarPreviewBodySchema }))
    .output(DecodeGrpcHarPreviewResponseSchema),
  encodeGrpcHarPreview: terraContract
    .meta({
      permissions: [PERMISSIONS.EXECUTE_TESTS.name],
    })
    .route({
      method: 'POST',
      path: '/encode-grpc-har-preview',
      summary:
        'Encode edited gRPC-web request JSON to wire bytes without persisting',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: EncodeGrpcHarPreviewBodySchema }))
    .output(EncodeGrpcHarPreviewResponseSchema),
});
